import { useEffect, useState, startTransition } from 'react';
import {
  Tabs,
  Tab,
  TextField,
  Button,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useSearchParams } from "react-router-dom";
import { useSnackbar } from './components/SnackbarService';
import clientService, { clearAuthTokens, saveAuthTokens } from '../clientService';
import { LoginRequest, UserRole, UserSignup } from '../api/Api';
import { useNavigate } from 'react-router';
import { useAppState } from 'src/state-management/state-provider';
import loginBg from 'src/resources/images/login_bg.jpg';
import logoImg from 'src/resources/images/logo-black 1.png';

const AuthPage = () => {
  const navigate = useNavigate();
  const { dispatch } = useAppState();
  const { showSnackbar, snackbarType } = useSnackbar();

  const [activeTab, setActiveTab] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showResendEmail, setShowResendEmail] = useState(false);
  const [isForgotPasswordMode, setIsForgotPasswordMode] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [isPasswordResetting, setIsPasswordResetting] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [resetEmailError, setResetEmailError] = useState('');
  
  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    email: '',
    password: '',
    termsAgree: false,
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchParams] = useSearchParams();
  const email_verification_token = searchParams.get("token");

  // Email validation helper
  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@(?:[a-zA-Z0-9-]+\.)+[a-zA-Z0-9-]+$/;
    return emailRegex.test(email);
  };

  useEffect(() => {
    if (email_verification_token) {
      const verifyEmail = async () => {
        setIsSubmitting(true);
        try {
          await clientService.api.verifyEmailAfterSignupApiV1AuthVerifyEmailPostSignupGet({ token: email_verification_token });
          showSnackbar({
            message: 'Email verified successfully! You can now log in.',
            type: snackbarType.SUCCESS,
          });
          setActiveTab(0);
        } catch (error: any) {
          const msg = error.response?.data?.detail || 'Email verification failed or link expired.';
          showSnackbar({ message: msg, type: snackbarType.ERROR });
        } finally { setIsSubmitting(false); }
      };
      verifyEmail();
    }
    clearAuthTokens();
    startTransition(() => {
      dispatch({ type: 'LOGOUT' });
    });
  }, []);

  const handleTabChange = (event: any, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleInputChange = (e: any) => {
    const { name, value, type, checked } = e.target;
    console.log(type, value);
    if (name === "email") {
      // Only show error if field has been touched and is invalid
      setEmailError(value && !validateEmail(value) ? "Invalid email address" : "");
    }
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleLogin = async () => {
    const loginPayload: LoginRequest = {
      email: formData.email,
      password: formData.password,
    };
    setIsSubmitting(true);
    try {
      const response = await clientService.api.loginApiV1AuthLoginPost(loginPayload);
      saveAuthTokens(response.data);
      const { access_token, refresh_token, ...rest } = response.data;
      startTransition(() => {
        dispatch({ type: 'SET_USER_DETAILS', payload: rest });
      });
      showSnackbar({ message: 'Login successful!' });
      if (rest.role.includes(UserRole.Admin) || rest.role.includes(UserRole.Employee)) {
        startTransition(() => {
          navigate('/admin');
        });
      } else {
        startTransition(() => {
          navigate('/landing');
        });
      }
    } catch (error: any) {
      console.error('Login failed:', error);
      const errorMessage = error.error?.detail || 'Login failed. Please try again.';
      showSnackbar({
        message: errorMessage,
        type: snackbarType.ERROR,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignup = async () => {
    setIsSubmitting(true);
    try {
      const signupPayload: UserSignup = {
        email: formData.email,
        password: formData.password,
        first_name: formData.firstname,
        last_name: formData.lastname,
        tnc_checked: formData.termsAgree,
      };
      const response = await clientService.api.signupApiV1AuthSignupPost(signupPayload);

      if (response.data.access_token) {
        saveAuthTokens(response.data);
        const { access_token, refresh_token, ...rest } = response.data;
        startTransition(() => {
          dispatch({ type: 'SET_USER_DETAILS', payload: rest });
        });
        showSnackbar({ message: 'Sign-up successful! You are now logged in.' });
        if (rest.role.includes(UserRole.User) || rest.role.includes(UserRole.Employee)) {
          startTransition(() => {
            navigate('/landing');
          });
        } else {
          startTransition(() => {
            navigate('/admin');
          });
        }
      } else {
        showSnackbar({
          message: 'Sign-up successful! Please check your email to verify your account or login.',
          type: snackbarType.SUCCESS,
        });
        setShowResendEmail(true);
      }
    } catch (error: any) {
      console.error('Signup failed:', error);
      const errorMessage = error.error?.detail || 'Signup failed. Please try again.';
      showSnackbar({
        message: errorMessage,
        type: snackbarType.ERROR,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendEmail = async () => {
    // Validate email before resending
    if (!formData.email || !validateEmail(formData.email)) {
      setEmailError(!formData.email ? "Email is required" : "Invalid email address");
      showSnackbar({
        message: 'Please enter a valid email address.',
        type: snackbarType.WARNING,
      });
      return;
    }
    
    setIsSubmitting(true);
    try {
      await clientService.api.sendVerificationEmailAfterSignupApiV1AuthSendVerificationEmailPostSignupPost({ email: formData.email });
      showSnackbar({
        message: 'Verification email resent! Please check your inbox.',
        type: snackbarType.SUCCESS,
      });
    } catch (error: any) {
      console.error('Resend verification email failed:', error);
      const errorMessage = error.error.data?.detail || 'Failed to resend verification email. Please try again.';
      showSnackbar({
        message: errorMessage,
        type: snackbarType.ERROR,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    // Validate email format
    if (!formData.email || !validateEmail(formData.email)) {
      setEmailError(!formData.email ? "Email is required" : "Invalid email address");
      showSnackbar({
        message: 'Please enter a valid email address.',
        type: snackbarType.WARNING,
      });
      return;
    }
    
    if (!formData.password) {
      showSnackbar({
        message: 'Password is required.',
        type: snackbarType.WARNING,
      });
      return;
    }
    
    if (activeTab === 1 && (!formData.firstname || !formData.lastname || !formData.termsAgree)) {
      showSnackbar({
        message: 'For signup, First Name, Last Name and agreement to Terms & Conditions are required.',
        type: snackbarType.WARNING,
      });
      return;
    }

    if (activeTab === 0) {
      handleLogin();
    } else {
      handleSignup();
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.1)), url(${loginBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 3,
      }}
    >

      <Box
        sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderRadius: 5,
          padding: 4,
          maxWidth: 450,
          width: '100%',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          backdropFilter: 'blur(10px)',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
          <img 
            src={logoImg} 
            alt="Darukaa Earth Logo" 
            style={{ maxWidth: '200px', height: 'auto', marginBottom: '12px' }}
          />
          <Typography
            variant="body2"
            sx={{
              color: '#666',
              fontSize: '0.9rem',
              fontWeight: 500,
              textAlign: 'center',
            }}
          >
            Innovating Carbon & Biodiversity Solutions
          </Typography>
        </Box>
          {!isForgotPasswordMode && (
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              centered
              className="auth-tabs"
              sx={{ pointerEvents: isSubmitting ? 'none' : 'auto' }}
            >
              <Tab label="Login" />
              <Tab label="Signup" />
            </Tabs>
          )}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            {/* LOGIN / PASSWORD RESET */}
            {activeTab === 0 && (
              <>
                {!isForgotPasswordMode ? (
                  <div>
                    <TextField
                      fullWidth
                      label="Email"
                      name="email"
                      type='email'
                      value={formData.email}
                      onChange={handleInputChange}
                      margin="normal"
                      variant="outlined"
                      className="auth-input"
                      disabled={isSubmitting}
                      required
                      error={!!emailError}
                      helperText={emailError}
                    />
                    <TextField
                      fullWidth
                      label="Password"
                      name="password"
                      required
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={handleInputChange}
                      margin="normal"
                      variant="outlined"
                      className="auth-input"
                      disabled={isSubmitting}
                      slotProps={{
                        input: {
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowPassword(!showPassword)}
                                edge="end"
                                disabled={isSubmitting}
                              >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        },
                      }}
                    />
                    <Box textAlign="right" mt={1}>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => setIsForgotPasswordMode(true)}
                        disabled={isSubmitting}
                      >
                        Forgot Password?
                      </Button>
                    </Box>
                  </div>
                ) : (
                  <Box mt={2}>
                    <TextField
                      fullWidth
                      label="Enter your registered email"
                      value={resetEmail}
                      type='email'
                      onChange={(e) => {
                        const value = e.target.value;
                        setResetEmail(value);
                        setResetEmailError(value && !validateEmail(value) ? "Invalid email address" : "");
                      }}
                      margin="normal"
                      variant="outlined"
                      className="auth-input"
                      disabled={isPasswordResetting}
                      required
                      error={!!resetEmailError}
                      helperText={resetEmailError}
                    />
                    <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      onClick={async () => {
                        if (!resetEmail || !validateEmail(resetEmail)) {
                          setResetEmailError(!resetEmail ? "Email is required" : "Invalid email address");
                          showSnackbar({
                            message: 'Please enter a valid email address.',
                            type: snackbarType.WARNING,
                          });
                          return;
                        }
                        setIsPasswordResetting(true);
                        try {
                          await clientService.api.requestPasswordResetApiV1RequestPasswordResetGet({
                            email: resetEmail,
                          });
                          showSnackbar({
                            message:
                              'If your email is registered, a password reset link has been sent.',
                            type: snackbarType.SUCCESS,
                          });
                          setIsForgotPasswordMode(false);
                          setResetEmail('');
                          setResetEmailError('');
                        } catch (error: any) {
                          const msg =
                            error.response?.data?.detail ||
                            'Failed to send password reset email.';
                          showSnackbar({ message: msg, type: snackbarType.ERROR });
                        } finally {
                          setIsPasswordResetting(false);
                        }
                      }}
                      disabled={isPasswordResetting}
                      sx={{ mt: 1 }}
                    >
                      {isPasswordResetting ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        'Send Password Reset Link'
                      )}
                    </Button>

                    <Box textAlign="center" mt={1}>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => {
                          setIsForgotPasswordMode(false);
                          setResetEmailError('');
                        }}
                        disabled={isPasswordResetting}
                      >
                        Back to Login
                      </Button>
                    </Box>
                  </Box>
                )}
              </>
            )}

            {/* SIGNUP */}
            {activeTab === 1 && (
              <div>
                <TextField
                  fullWidth
                  label="First Name"
                  name="firstname"
                  value={formData.firstname}
                  onChange={handleInputChange}
                  margin="normal"
                  variant="outlined"
                  className="auth-input"
                  disabled={isSubmitting}
                  required
                />
                <TextField
                  fullWidth
                  label="Last Name"
                  name="lastname"
                  value={formData.lastname}
                  onChange={handleInputChange}
                  margin="normal"
                  variant="outlined"
                  className="auth-input"
                  disabled={isSubmitting}
                  required
                />
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  value={formData.email}
                  type='email'
                  onChange={handleInputChange}
                  margin="normal"
                  variant="outlined"
                  className="auth-input"
                  disabled={isSubmitting}
                  placeholder='example@org.com'
                  required
                  error={!!emailError}
                  helperText={emailError}
                />
                <TextField
                  fullWidth
                  label="Password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleInputChange}
                  margin="normal"
                  variant="outlined"
                  className="auth-input"
                  disabled={isSubmitting}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            disabled={isSubmitting}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
                <Checkbox
                  name="termsAgree"
                  checked={formData.termsAgree}
                  onChange={handleInputChange}
                  className="auth-checkbox"
                  disabled={isSubmitting}
                />
                <span>
                  I agree to the{' '}
                  <a
                    href="#"
                    onClick={() => !isSubmitting && setDialogOpen(true)}
                    style={{ pointerEvents: isSubmitting ? 'none' : 'auto', color: isSubmitting ? 'grey' : 'inherit' }}
                  >
                    Terms & Conditions
                  </a>
                </span>
              </div>
            )}
            { !isForgotPasswordMode && (
            <Button
              fullWidth
              variant="contained"
              color="primary"
              type="submit"
              onClick={handleSubmit}
              className="auth-button"
              disabled={isSubmitting}
              sx={{ mt: 2 }}
            >
              {isSubmitting ? <CircularProgress size={24} color="inherit" /> : activeTab === 0 ? 'Login' : 'Signup'}
            </Button>
            ) }
            { showResendEmail && activeTab === 1 && (
             <Box textAlign="center" mt={1}>
                      <Button
                        variant="text"
                        size="small"
                        onClick={handleResendEmail}
                        disabled={isSubmitting}
                      >
                        resend verification email or contact "registrations@darukaa.com"
                      </Button>
                    </Box> 
            ) }
          </form>

          <Dialog 
            open={dialogOpen} 
            onClose={() => setDialogOpen(false)}
            maxWidth="md"
            fullWidth
            sx={{
              '& .MuiDialog-paper': {
                maxHeight: '90vh',
              },
            }}
          >
            <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 600, borderBottom: '1px solid #e0e0e0', pb: 2 }}>
              Terms and Conditions
            </DialogTitle>
            <DialogContent sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Effective Date:</strong> November 15, 2025<br />
                <strong>Last Updated:</strong> November 15, 2025
              </Typography>
              
              <Typography variant="body1" sx={{ mt: 3, mb: 2 }}>
                Welcome to <strong>Darukaa.Earth</strong>! These Terms and Conditions ("Terms") govern your use of our geospatial software-as-a-service platform for climate intelligence, carbon analysis, biodiversity assessment, and environmental monitoring ("Service"). By creating an account or using the Service, you agree to these Terms.
              </Typography>

              <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
                1. Use of Service
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                We grant you a limited, non-exclusive license to use the Service for your business or personal purposes.
                You may not copy, resell, redistribute, reverse engineer, or misuse the Service or its data.
                You are responsible for keeping your account credentials secure and for all activities under your account.
                You must be at least 18 years old to use the Service.
              </Typography>

              <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                2. Data and Content
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                All platform software, datasets, algorithms, maps, and analytical content belong to Darukaa Earth or its partners.
                Any geospatial data, projects, or content you upload remains your property, but you grant us permission to process it to provide the Service.
                We may use third-party data sources, satellite imagery, and APIs subject to their respective terms.
                You represent that you have rights to any data you upload.
              </Typography>

              <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                3. Service Features
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                The Service includes carbon stock analysis, biodiversity assessments, habitat health monitoring, and environmental impact analysis.
                Certain advanced features, API access, or premium datasets may require a paid subscription.
                We reserve the right to modify, suspend, or discontinue features with reasonable notice.
              </Typography>

              <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                4. Payments
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Paid subscriptions or one-time purchases must be settled according to the plan you select.
                Unless otherwise stated, payments are non-refundable, except as required by applicable law.
                You are responsible for all taxes applicable to your purchases.
                We may change pricing with 30 days' notice to subscribers.
              </Typography>

              <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                5. Service Availability
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                We aim for high availability but cannot guarantee uninterrupted access.
                Maintenance, updates, third-party service outages, or external factors may cause downtime.
                We are not liable for delays or failures due to circumstances beyond our reasonable control.
              </Typography>

              <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                6. Privacy
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Your personal data and project information will be handled according to our Privacy Policy.
                We use industry-standard security measures but cannot guarantee absolute security.
                By using the Service, you consent to our data processing practices.
              </Typography>

              <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                7. Disclaimers
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                The Service is provided "as is" and "as available" without warranties of any kind, express or implied.
                Geospatial data, satellite imagery, and analytical results may contain inaccuracies or limitations.
                You should not rely solely on our data for decisions where precision is critical (e.g., safety, legal compliance, or financial planning).
                We do not warrant that the Service will meet your specific requirements or be error-free.
              </Typography>

              <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                8. Limitation of Liability
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                To the extent permitted by law, our total liability is limited to the amount you paid for the Service in the past 12 months.
                We are not responsible for indirect, consequential, incidental, or punitive damages.
                You agree to indemnify us against claims arising from your breach of these Terms or misuse of the Service.
              </Typography>

              <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                9. Termination
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                We may suspend or terminate your access if you breach these Terms, engage in fraud, or misuse the Service.
                You may close your account at any time through the platform or by contacting us.
                Upon termination, your access to the Service will cease, and we may delete your data in accordance with our data retention policy.
              </Typography>

              <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                10. Intellectual Property
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                All trademarks, logos, and service marks belong to Darukaa Earth or their respective owners.
                You may not use our branding without prior written permission.
                You retain ownership of your project data but grant us a license to process it to provide the Service.
              </Typography>

              <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                11. Governing Law
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                These Terms are governed by the laws of India. Any disputes must be resolved in the courts of Bombay High Court, Maharashtra, India.
                If any provision is found unenforceable, the remaining provisions will remain in effect.
              </Typography>

              <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                12. Changes to Terms
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                We may update these Terms to reflect changes in our Service or for legal reasons.
                Material changes will be communicated via email or through the platform.
                Continued use of the Service after changes means you accept the updated Terms.
              </Typography>

              <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
                Contact Us
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Questions about these Terms? Contact us at:<br />
                <strong>Email:</strong> compliance@darukaa.com<br />
                <strong>Company:</strong> Darukaa Earth<br />
                <strong>Website:</strong> https://www.darukaa.earth
              </Typography>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3, fontStyle: 'italic' }}>
                By accepting these Terms, you acknowledge that you have read, understood, and agree to be bound by them.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e0e0e0' }}>
              <Button 
                onClick={() => setDialogOpen(false)} 
                variant="contained"
                color="primary"
                fullWidth
                sx={{ fontWeight: 600 }}
              >
                I Understand
              </Button>
            </DialogActions>
          </Dialog>
      </Box>
    </Box>
  );
};

export default AuthPage;